export type Release = {
  release: string;
  timestamp: string;
  host: string;
  remotes: Record<string, string>;
};

export type Manifest = {
  current: string;
  releases: Release[];
};

export const byTimestamp = (releases: Release[]) =>
  [...releases].sort((one, other) => one.timestamp.localeCompare(other.timestamp));

export const resolveRelease = (manifest: Manifest, hostVersion: string) => {
  const current = manifest.releases.find((release) => release.release === manifest.current);

  if (current?.host === hostVersion) {
    return current;
  }

  return byTimestamp(manifest.releases.filter((release) => release.host === hostVersion)).at(-1);
};

export const remoteManifestUrl = (remote: string, version: string) => `/${remote}/${version}/mf-manifest.json`;

export const HOST_MODULE = 'host';

export const reachableVersions = (manifest: Manifest) => {
  const reachable: Record<string, Set<string>> = {};

  const keep = (module: string, version: string) => {
    reachable[module] ??= new Set();
    reachable[module].add(version);
  };

  manifest.releases.forEach((release) => {
    keep(HOST_MODULE, release.host);
    Object.entries(release.remotes).forEach(([remote, version]) => keep(remote, version));
  });

  return Object.fromEntries(
    Object.entries(reachable).map(([module, versions]) => [module, [...versions].sort()]),
  ) as Record<string, string[]>;
};

export const unreachableVersions = (manifest: Manifest, published: Record<string, string[]>) => {
  const reachable = reachableVersions(manifest);

  return Object.entries(published)
    .filter(([module]) => reachable[module])
    .flatMap(([module, versions]) =>
      versions.filter((version) => !reachable[module].includes(version)).map((version) => `${module}/${version}`),
    );
};
